import React, { useEffect, useState } from "react";
import { apiAdmin, BACKEND_URL } from "../../lib/api";
import { Plus, Trash2, Edit3, X, Upload, Grid } from "lucide-react";
import { toast } from "sonner";

const ICONS = ["Box", "Palette", "Sparkles", "Package", "Gift", "Shirt", "Sticker", "BookOpen", "Image", "Star"];
const empty = { name: "", slug: "", subtitle: "", description: "", image_url: "", icon: "Box", accent_color: "#7C3AED", order: 0, active: true };

const slugify = (s) =>
  s.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const AdminSections = () => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);

  const load = () => apiAdmin.get("/sections/all").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...empty, ...s });
    setShowForm(true);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiAdmin.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: `${BACKEND_URL}${res.data.url}` }));
      toast.success("Imagen subida");
    } catch { toast.error("Error al subir"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.name) { toast.error("Nombre requerido"); return; }
    const payload = { ...form, slug: form.slug || slugify(form.name) };
    try {
      if (editing) await apiAdmin.put(`/sections/${editing.id}`, payload);
      else await apiAdmin.post("/sections", payload);
      toast.success(editing ? "Actualizada" : "Creada");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar sección? Los productos/categorías con esta sección NO se eliminan.")) return;
    await apiAdmin.delete(`/sections/${id}`);
    toast.success("Eliminada");
    load();
  };

  return (
    <div data-testid="admin-sections">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div>
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-yolk">─ Secciones ─</span>
          <h1 className="mt-2 font-heading text-4xl font-bold text-brand-ink">Secciones del catálogo</h1>
          <p className="text-gray-600 mt-1 text-sm">Son los "mundos" principales de tu tienda (ej. Figuras 3D, Papelería). Se muestran en el menú y en el home.</p>
        </div>
        <button onClick={openNew} data-testid="section-new-btn" className="btn-chunky inline-flex items-center gap-2 bg-mint-deep text-white px-5 py-3 text-sm shrink-0">
          <Plus className="h-4 w-4" /> Nueva sección
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {items.map((s) => (
          <div key={s.id} data-testid={`section-card-${s.id}`} className="bg-white border-2 border-black rounded-3xl overflow-hidden">
            {s.image_url ? (
              <div className="aspect-[5/2] relative" style={{ backgroundColor: s.accent_color }}>
                <img src={s.image_url} alt={s.name} className="w-full h-full object-cover opacity-90" />
              </div>
            ) : (
              <div className="aspect-[5/2] flex items-center justify-center" style={{ backgroundColor: s.accent_color }}>
                <Grid className="h-12 w-12 text-white" />
              </div>
            )}
            <div className="p-5">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-heading font-extrabold text-brand-ink text-xl">{s.name}</h3>
                <code className="text-xs bg-mint-light px-2 py-0.5 rounded-full">/{s.slug}</code>
                {!s.active && <span className="text-xs font-bold text-red-500">INACTIVA</span>}
              </div>
              {s.subtitle && <p className="text-sm italic text-mint-dark mb-1">{s.subtitle}</p>}
              <p className="text-sm text-gray-600 line-clamp-2 mb-4">{s.description}</p>
              <div className="flex gap-2">
                <button onClick={() => openEdit(s)} data-testid={`section-edit-${s.id}`} className="btn-soft flex-1 inline-flex items-center justify-center gap-2 bg-white px-3 py-2 text-xs">
                  <Edit3 className="h-3 w-3" /> Editar
                </button>
                <button onClick={() => remove(s.id)} data-testid={`section-delete-${s.id}`} className="btn-soft inline-flex items-center justify-center px-3 py-2 bg-red-50 text-red-500">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white border-2 border-black rounded-3xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b-2 border-black sticky top-0 bg-white z-10">
              <h2 className="font-heading text-xl font-bold">{editing ? "Editar" : "Nueva"} sección</h2>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-yolk-light rounded-full"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <F label="Nombre *" value={form.name} onChange={(v) => setForm({...form, name: v, slug: form.slug || slugify(v)})} testid="section-form-name" />
              <F label="Slug (URL)" value={form.slug} onChange={(v) => setForm({...form, slug: slugify(v)})} mono testid="section-form-slug" />
              <F label="Subtítulo (itálico decorativo)" value={form.subtitle} onChange={(v) => setForm({...form, subtitle: v})} testid="section-form-subtitle" />
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Descripción</label>
                <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} data-testid="section-form-description" className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-mint-deep resize-none" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] font-bold mb-2">Icono</label>
                  <select value={form.icon} onChange={(e) => setForm({...form, icon: e.target.value})} data-testid="section-form-icon" className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm bg-white">
                    {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] font-bold mb-2">Color acento</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={form.accent_color} onChange={(e) => setForm({...form, accent_color: e.target.value})} data-testid="section-form-color" className="h-11 w-16 border-2 border-black rounded-xl cursor-pointer" />
                    <input type="text" value={form.accent_color} onChange={(e) => setForm({...form, accent_color: e.target.value})} className="flex-1 border-2 border-black rounded-xl px-3 py-2 text-xs font-mono" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold mb-2">Imagen de portada</label>
                <input type="text" value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="URL o subir" data-testid="section-form-image-url" className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm mb-3" />
                <label className="btn-chunky inline-flex items-center gap-2 bg-yolk text-white px-4 py-2 cursor-pointer text-sm">
                  <Upload className="h-4 w-4" />
                  {uploading ? "Subiendo..." : "Subir imagen"}
                  <input type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="section-form-upload" />
                </label>
                {form.image_url && <img src={form.image_url} alt="preview" className="mt-3 h-28 w-40 object-cover border-2 border-black rounded-xl" />}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] font-bold mb-2">Orden</label>
                  <input type="number" value={form.order} onChange={(e) => setForm({...form, order: Number(e.target.value)})} data-testid="section-form-order" className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm" />
                </div>
                <label className="flex items-center gap-3 mt-7">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})} data-testid="section-form-active" className="h-4 w-4" />
                  <span className="text-sm">Activa</span>
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setShowForm(false)} className="btn-chunky flex-1 bg-white text-black py-3 text-sm">Cancelar</button>
                <button onClick={submit} data-testid="section-form-submit" className="btn-chunky flex-1 bg-mint-deep text-white py-3 text-sm">Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const F = ({ label, value, onChange, mono = false, testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">{label}</label>
    <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={`w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-mint-deep ${mono ? "font-mono" : ""}`} />
  </div>
);

export default AdminSections;
